import { useEffect, useMemo, useState, type FormEvent } from "react";

import { Chevron } from "@/components/ui/Chevron";
import { explainStep, submitParameters } from "@/lib/api/sessions";
import type { ParametersSchemaDoc } from "@/lib/api/types";

/**
 * JSON-Schema-driven parameter form.
 *
 * For primitive types (string/number/boolean/enum) we render a real form
 * control. For object / array fields (e.g. source_endpoint), we render a
 * monospaced JSON textarea — the user pastes valid JSON, which we parse
 * on submit. Server-side validation catches anything we miss.
 *
 * This is the pragmatic v0.1 approach. A deeper Phase-4 iteration would
 * render nested forms for objects and dynamic list editors for arrays.
 */
export function ParameterForm({
  sessionId,
  schema,
}: {
  sessionId: string;
  schema: ParametersSchemaDoc;
}) {
  const fields = useMemo(() => fieldsFromSchema(schema), [schema]);

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.placeholder ?? ""])),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explanationLoading, setExplanationLoading] = useState(true);

  // Step 4: LLM commentary for the parameters step.
  useEffect(() => {
    const ctrl = new AbortController();
    setExplanationLoading(true);
    explainStep(sessionId, { step: "parameters" }, ctrl.signal)
      .then((r) => setExplanation(r.text))
      .catch(() => setExplanation(null))
      .finally(() => setExplanationLoading(false));
    return () => ctrl.abort();
  }, [sessionId]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const params: Record<string, unknown> = {};
    const errs: Record<string, string> = {};

    for (const f of fields) {
      const raw = values[f.name] ?? "";
      if (!raw && !schema.required.includes(f.name)) continue;
      try {
        params[f.name] = coerceField(f, raw);
      } catch (err) {
        errs[f.name] = err instanceof Error ? err.message : "Invalid value.";
      }
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      await submitParameters(sessionId, { params });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <Chevron className="mr-2" /> deploy parameters
        </p>
        <h2 className="text-xl font-semibold mt-1">
          {schema.pattern_name}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Required fields are marked with *. Object / array fields take
          JSON.
        </p>
      </header>

      <aside className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
          <Chevron className="mr-2" /> what you're configuring
        </p>
        {explanationLoading ? (
          <p className="text-sm text-muted-foreground italic">Generating explanation…</p>
        ) : explanation ? (
          <p className="text-sm leading-relaxed">{explanation}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            (No explanation available.)
          </p>
        )}
      </aside>

      <form
        onSubmit={onSubmit}
        className="rounded-lg border border-border/60 bg-card p-6 space-y-4"
      >
        {fields.map((f) => (
          <SchemaField
            key={f.name}
            field={f}
            value={values[f.name] ?? ""}
            onChange={(v) =>
              setValues((prev) => ({ ...prev, [f.name]: v }))
            }
            error={fieldErrors[f.name]}
            required={schema.required.includes(f.name)}
          />
        ))}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center justify-end pt-2 border-t border-border/60">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? "Submitting…" : "Submit parameters"}
          </button>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface SchemaField {
  name: string;
  type: "string" | "number" | "integer" | "boolean" | "enum" | "json";
  description?: string;
  enumOptions?: string[];
  placeholder?: string;
}

function fieldsFromSchema(schema: ParametersSchemaDoc): SchemaField[] {
  const props = (schema.schema?.properties as Record<string, Record<string, unknown>>) ?? {};
  return Object.entries(props).map(([name, def]) => {
    const type = typeOf(def);
    const enumOptions = Array.isArray(def.enum) ? (def.enum as string[]) : undefined;
    return {
      name,
      type: enumOptions ? "enum" : type,
      description: typeof def.description === "string" ? def.description : undefined,
      enumOptions,
      placeholder: type === "json" ? '{ "url": "...", "auth_mode": "managed-identity" }' : undefined,
    };
  });
}

function typeOf(def: Record<string, unknown>): SchemaField["type"] {
  const raw = def.type;
  if (raw === "boolean") return "boolean";
  if (raw === "integer") return "integer";
  if (raw === "number") return "number";
  if (raw === "string") return "string";
  // object / array / unknown → JSON textarea
  return "json";
}

function coerceField(f: SchemaField, raw: string): unknown {
  switch (f.type) {
    case "string":
    case "enum":
      return raw;
    case "number":
      if (raw === "") return null;
      return Number.parseFloat(raw);
    case "integer":
      if (raw === "") return null;
      return Number.parseInt(raw, 10);
    case "boolean":
      return raw === "true";
    case "json":
      try {
        return JSON.parse(raw);
      } catch (err) {
        throw new Error(`Invalid JSON: ${(err as Error).message}`);
      }
    default:
      return raw;
  }
}

function SchemaField({
  field,
  value,
  onChange,
  error,
  required,
}: {
  field: SchemaField;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required: boolean;
}) {
  const id = `param-${field.name}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {field.name}
        {required ? <span className="text-destructive">*</span> : null}
      </label>
      {field.description ? (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      ) : null}
      {renderControl(id, field, value, onChange)}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function renderControl(
  id: string,
  field: SchemaField,
  value: string,
  onChange: (v: string) => void,
) {
  const base =
    "rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  if (field.type === "enum" && field.enumOptions) {
    return (
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={base}
      >
        <option value="">(select)</option>
        {field.enumOptions.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={base}
      >
        <option value="">(select)</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (field.type === "json") {
    return (
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={`${base} font-mono resize-y`}
      />
    );
  }
  return (
    <input
      id={id}
      type={field.type === "number" || field.type === "integer" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={base}
    />
  );
}
